import moment from "moment-timezone";
import { OtcContractLayerDto } from "@/lib/api/otc";
import {
  convertFormatMomentToLocalTimezone,
  formatDateWithoutTimezoneToDate,
  safeParseMomentWithFormats,
} from "@/lib/api/date";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { contractToForm } from "../contract-form-v2/transform";
import { OtcFormValues } from "../contract-form-v2/types";

/**
 * Form-value shape used by the layer-native v3 editor.
 *
 * It is a superset of the v2 `OtcFormValues` because the current layer can own
 * fields that are NOT `trading_contract` columns and therefore never appear in
 * a contract snapshot or in v2's form shape. The clearest example is
 * `exit_date`, which is layer/event metadata.
 */
export interface OtcLayerFormValues extends OtcFormValues {
  exit_date?: Date;
}

/**
 * Layer metadata date fields that are not contract columns. They are not
 * produced by `contractToForm` (which only reads scalar contract fields), so
 * they must be seeded from the layer DTO's `field_patch`.
 */
const LAYER_METADATA_DATE_FIELDS = ["exit_date"] as const;

/**
 * Parses a scalar ISO/date-like string into a Date, mirroring v2's date
 * parsing conventions (timezone-aware, via moment) so values round-trip
 * consistently with the rest of the OTC forms.
 */
export function parseLayerDate(value: unknown): Date | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return formatDateWithoutTimezoneToDate(value);
  if (typeof value === "string" && value !== "") {
    const formats = [
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "YYYY-MM-DDTHH:mm:ss",
      moment.ISO_8601,
    ].reverse();
    const parsed = safeParseMomentWithFormats(
      value,
      formats,
      CommonDestinationTimezone,
    );
    return parsed
      ? convertFormatMomentToLocalTimezone(parsed)
      : formatDateWithoutTimezoneToDate(new Date(value));
  }
  return undefined;
}

/**
 * Adapts a current layer DTO into editable v3 form values.
 *
 * - Contract scalar fields come from the layer `snapshot` via the v2
 *   `contractToForm` transform (a pure scalar mapping; safe to reuse).
 * - Layer metadata fields (e.g. `exit_date`) are seeded from `field_patch`,
 *   since the DTO does not expose layer `meta_data` and these are not contract
 *   columns.
 *
 * Use this only for the current editable layer. Historical/locked layers are
 * rendered read-only directly from their own snapshot/field_patch and are not
 * adapted into a form.
 */
export function layerToFormValues(layer: OtcContractLayerDto): OtcLayerFormValues {
  const base = contractToForm(layer.snapshot);
  const values: OtcLayerFormValues = { ...base };

  for (const field of LAYER_METADATA_DATE_FIELDS) {
    const patched = layer.field_patch[field];
    const parsed = parseLayerDate(patched);
    if (parsed) {
      values[field] = parsed;
    }
  }

  return values;
}
